import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import AiTextBlockNode from '../components/aicanvas/blocks/AiTextBlockNode';
import ImageBlockNode from '../components/aicanvas/blocks/ImageBlockNode';
import ChartBlockNode from '../components/aicanvas/blocks/ChartBlockNode';

/**
 * Custom TipTap node types for AI-native canvas content (spec 044). `StarterKit` alone has
 * no embed-node concept — these are atomic, non-editable-inline block nodes carrying an
 * `origin` attribute ('AI' | 'STUDENT') that drives the visual distinction required by
 * FR-024 (research.md Decision 7).
 */

const originAttribute = {
  origin: {
    default: 'AI',
    parseHTML: (element: HTMLElement) => element.getAttribute('data-origin') || 'AI',
    renderHTML: (attributes: Record<string, any>) => ({ 'data-origin': attributes.origin }),
  },
};

export const AiTextBlock = Node.create({
  name: 'aiTextBlock',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      ...originAttribute,
      text: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-ai-text-block]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-ai-text-block': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AiTextBlockNode);
  },
});

export const ImageBlock = Node.create({
  name: 'imageBlock',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      ...originAttribute,
      url: { default: '' },
      alt: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-image-block]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-image-block': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageBlockNode);
  },
});

export const ChartBlock = Node.create({
  name: 'chartBlock',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      ...originAttribute,
      chartSpec: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-chart-block]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-chart-block': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChartBlockNode);
  },
});

export const aiCanvasNodeExtensions = [AiTextBlock, ImageBlock, ChartBlock];
